import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { BookOpen, ExternalLink, FileText, Globe, LayoutTemplate, Lock, Plus, Snowflake, SquareDashed } from "lucide-react";
import type { MenuSummaryDto } from "@dishfolio/contracts";
import { THEMES, getTheme } from "@dishfolio/themes";
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, Skeleton, cn } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useMenus, useQueryClient, useRestaurants } from "../lib/queries.ts";
import { relativeTime } from "../lib/format.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError } from "../components/Upgrade.tsx";

export function MenuStatus({ menu }: { menu: MenuSummaryDto }) {
  if (menu.status === "published")
    return menu.hasUnpublishedChanges ? <Badge variant="warning">Published · unpublished changes</Badge> : <Badge variant="success">Published</Badge>;
  return <Badge variant="secondary">Draft</Badge>;
}

export function MenuCard({ menu, restaurantName, frozen }: { menu: MenuSummaryDto; restaurantName?: string; frozen?: boolean }) {
  const theme = getTheme(menu.themeId);
  return (
    <Link to="/menus/$menuId" params={{ menuId: menu.id }} className="group block">
      <Card className="h-full gap-3 p-0 transition group-hover:border-primary/40 group-hover:shadow-md">
        <div className="flex h-24 overflow-hidden rounded-t-xl" style={{ background: theme?.tokens.backgroundColor }}>
          {theme?.preview.swatches.map((c) => (
            <span key={c} className="flex-1 opacity-90" style={{ background: c }} />
          ))}
        </div>
        <div className="grid gap-2 px-4 pb-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-semibold">{menu.name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {restaurantName ? `${restaurantName} · ` : ""}
                {theme?.name ?? menu.themeId} · {menu.enabledLanguages.map((l) => l.toUpperCase()).join(" ")}
              </div>
            </div>
            {frozen ? (
              <Badge variant="destructive">
                <Snowflake /> Read-only
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <MenuStatus menu={menu} />
            <span>Edited {relativeTime(menu.updatedAt)}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function NewMenuDialog({ open, onOpenChange, defaultRestaurantId }: { open: boolean; onOpenChange: (o: boolean) => void; defaultRestaurantId?: string }) {
  const { org } = useOrg();
  const restaurants = useRestaurants();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("Menu");
  const [restaurantId, setRestaurantId] = useState(defaultRestaurantId ?? "");
  const [template, setTemplate] = useState<"theme" | "blank" | "sample" | "website" | "pdf">("theme");
  const [themeId, setThemeId] = useState("basic");
  const [loading, setLoading] = useState(false);
  const plan = org?.plan;
  const rid = restaurantId || restaurants.data?.[0]?.id || "";
  const themeAllowed = (k: string) => plan?.themes === "all" || plan?.themes.includes(k);
  const create = async () => {
    if (template === "website" || template === "pdf") {
      onOpenChange(false);
      await navigate({ to: "/imports/new", search: { type: template, restaurantId: rid } });
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ menu: MenuSummaryDto }>("/menus", { restaurantId: rid, name, themeId: template === "blank" ? "basic" : themeId, template });
      await qc.invalidateQueries({ queryKey: ["menus"] });
      onOpenChange(false);
      await navigate({ to: "/menus/$menuId", params: { menuId: res.menu.id } });
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New menu</DialogTitle>
          <DialogDescription>Start from a theme, a blank page, or import an existing website or PDF.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="mname">
            <Input id="mname" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Location" htmlFor="mrest">
            <NativeSelect id="mrest" value={rid} onChange={(e) => setRestaurantId(e.target.value)}>
              {restaurants.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        <div className="grid gap-2 sm:grid-cols-5">
          {(
            [
              ["theme", <LayoutTemplate key="t" />, "Theme", false],
              ["sample", <BookOpen key="s" />, "Sample", false],
              ["blank", <SquareDashed key="b" />, "Blank", false],
              ["website", <Globe key="w" />, "Website", !plan?.imports],
              ["pdf", <FileText key="p" />, "PDF", !plan?.imports],
            ] as const
          ).map(([key, icon, label, locked]) => (
            <button
              key={key}
              type="button"
              onClick={() => (locked ? notifyError({ isPlanLimit: true, message: "Imports require the Pro plan" }) : setTemplate(key))}
              className={cn("flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border p-3 text-sm [&_svg]:size-5", template === key && "border-primary bg-primary/5 text-primary", locked && "opacity-60")}
            >
              {icon}
              <span className="flex items-center gap-1">
                {label} {locked ? <Lock className="size-3" /> : null}
              </span>
            </button>
          ))}
        </div>
        {template === "theme" || template === "sample" ? (
          <div className="grid grid-cols-3 gap-2">
            {THEMES.map((t) => (
              <button
                key={t.key}
                type="button"
                disabled={!themeAllowed(t.key)}
                onClick={() => setThemeId(t.key)}
                className={cn("flex flex-col gap-1.5 rounded-lg border p-2 text-left disabled:cursor-not-allowed disabled:opacity-50", themeId === t.key && "border-primary ring-2 ring-primary/20")}
              >
                <div className="flex h-9 overflow-hidden rounded">
                  {t.preview.swatches.map((c) => (
                    <span key={c} className="flex-1" style={{ background: c }} />
                  ))}
                </div>
                <span className="flex items-center justify-between text-xs font-medium">
                  {t.name}
                  {!themeAllowed(t.key) ? <Lock className="size-3" /> : null}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={create} loading={loading} disabled={!rid}>
            {template === "website" || template === "pdf" ? "Continue to import" : "Create menu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MenusPage() {
  const menus = useMenus();
  const restaurants = useRestaurants();
  const { org, can } = useOrg();
  const [open, setOpen] = useState(false);
  const atLimit = org && menus.data && menus.data.length >= org.plan.maxMenus;
  return (
    <PageContainer wide>
      <PageHeader
        title="Menus"
        description={org ? `${menus.data?.length ?? 0} of ${org.plan.maxMenus} menus on the ${org.plan.name} plan.` : undefined}
        actions={
          can("menu:edit") ? (
            <Button onClick={() => (atLimit ? notifyError({ isPlanLimit: true, message: `Your ${org?.plan.name} plan allows ${org?.plan.maxMenus} menu(s).` }) : setOpen(true))}>
              <Plus /> New menu
            </Button>
          ) : null
        }
      />
      {menus.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-44" />
          ))}
        </div>
      ) : menus.data?.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {menus.data.map((m) => (
            <MenuCard key={m.id} menu={m} restaurantName={restaurants.data?.find((r) => r.id === m.restaurantId)?.name} />
          ))}
        </div>
      ) : (
        <EmptyState icon={<BookOpen />} title="No menus yet" description="Create your first menu from a theme or import one." action={<Button onClick={() => setOpen(true)}>Create a menu</Button>} />
      )}
      <NewMenuDialog open={open} onOpenChange={setOpen} />
      {menus.data?.some((m) => m.status === "published") ? (
        <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
          <ExternalLink className="size-3" /> Published menus are live at their public address even when you’re editing.
        </p>
      ) : null}
    </PageContainer>
  );
}
